import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import ContentCard from '@/components/base/ContentCard';
import SectionHeading from '@/components/base/SectionHeading';
import ShareModal from './components/ShareModal';
import { useFavorites } from '@/hooks/useFavorites';
import { usePageMeta } from '@/hooks/usePageMeta';
import {
  formatDate,
  formatViews,
  getArticleById,
  getCategoryName,
  getHotArticles,
  getRelatedArticles,
} from '@/utils/content';

export default function Detail() {
  const { contentId = '' } = useParams();
  const article = getArticleById(contentId);
  const { isFavorite, toggleFavorite } = useFavorites();
  usePageMeta(
    article ? `${article.title} - 鲁啊鲁` : '内容未找到 - 鲁啊鲁',
    article ? article.summary : '抱歉，这篇内容不存在或已被移除。',
  );
  const [shareOpen, setShareOpen] = useState(false);
  const [toast, setToast] = useState('');

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(''), 2200);
  };

  if (!article) {
    return (
      <div className="w-full px-4 md:px-8 py-16 text-center">
        <span className="inline-flex w-16 h-16 items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-5">
          <i className="ri-file-unknow-line text-3xl"></i>
        </span>
        <h1 className="text-xl font-bold text-foreground-950">内容不存在</h1>
        <p className="mt-2 text-sm text-foreground-500">这篇内容可能已被移除或链接有误。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const favorite = isFavorite(article.id);
  const related = getRelatedArticles(article, 4);
  const hot = getHotArticles(5).filter((item) => item.id !== article.id).slice(0, 4);
  const categoryName = getCategoryName(article.category);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      showToast('链接已复制到剪贴板');
    } catch {
      showToast('复制失败，请手动复制浏览器地址');
    }
  };

  const handleSocial = (platform: string) => {
    if (platform === 'weibo') {
      const url = `https://service.weibo.com/share/share.php?title=${encodeURIComponent(article.title)}&url=${encodeURIComponent(window.location.href)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    handleCopy();
    if (platform === 'wechat' || platform === 'moments') {
      showToast('链接已复制，请在微信中粘贴分享');
    } else {
      showToast('链接已复制，可粘贴到聊天窗口');
    }
  };

  const handleFavorite = () => {
    const added = toggleFavorite(article.id);
    showToast(added ? '已加入我的收藏' : '已取消收藏');
  };

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8 pb-24 lg:pb-8">
      <Breadcrumb
        items={[
          { label: '首页', to: '/' },
          { label: categoryName, to: `/category/${article.category}` },
          { label: article.title },
        ]}
      />

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <article className="lg:col-span-8">
          <span className="inline-flex rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
            {categoryName}
          </span>
          <h1 className="mt-4 font-heading text-2xl md:text-3xl font-bold leading-snug text-foreground-950">
            {article.title}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <span className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-50 text-primary-600 text-xs font-bold">
                {article.author.slice(0, 1)}
              </span>
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              {formatDate(article.date)}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line"></i>
              {formatViews(article.views)} 阅读
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line"></i>
              {article.readTime} 分钟读完
            </span>
          </div>

          <div className="mt-6 w-full h-64 sm:h-96 rounded-lg overflow-hidden bg-background-200">
            <img
              src={article.cover}
              alt={`${article.title} 封面`}
              title={`${article.title} 鲁啊鲁资讯`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="hidden lg:flex items-center gap-3 mt-6">
            <button
              type="button"
              onClick={handleFavorite}
              className={`inline-flex items-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                favorite
                  ? 'bg-accent-500 text-background-50 hover:bg-accent-600'
                  : 'border border-background-200 text-foreground-600 hover:border-accent-300 hover:text-accent-600'
              }`}
            >
              <i className={favorite ? 'ri-heart-3-fill' : 'ri-heart-3-line'}></i>
              {favorite ? '已收藏' : '收藏'}
            </button>
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-200 px-4 py-2 text-sm font-medium text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-colors"
            >
              <i className="ri-share-forward-line"></i>
              分享
            </button>
          </div>

          <div className="mt-8 space-y-5">
            {article.content.map((block, index) => {
              if (block.type === 'quote') {
                return (
                  <blockquote
                    key={`quote-${index}`}
                    className="border-l-4 border-accent-400 bg-accent-50 px-5 py-4 text-base italic leading-relaxed text-foreground-700"
                  >
                    {block.text}
                  </blockquote>
                );
              }
              if (block.type === 'img') {
                return (
                  <figure key={`img-${index}`} className="w-full">
                    <div className="w-full h-64 md:h-80 rounded-lg overflow-hidden bg-background-200">
                      <img
                        src={block.src}
                        alt={block.caption ?? article.title}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    {block.caption ? (
                      <figcaption className="mt-2 text-center text-xs text-foreground-400">
                        {block.caption}
                      </figcaption>
                    ) : null}
                  </figure>
                );
              }
              return (
                <p key={`p-${index}`} className="text-base leading-8 text-foreground-700">
                  {block.text}
                </p>
              );
            })}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-2">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-secondary-50 border border-secondary-200 px-3 py-1 text-xs text-secondary-700"
              >
                #{tag}
              </span>
            ))}
          </div>

          <div className="mt-10 rounded-lg border border-background-200 bg-background-50 p-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-6 h-6 flex items-center justify-center text-primary-600">
                <i className="ri-chat-3-line text-xl"></i>
              </span>
              <h2 className="font-heading text-lg font-bold text-foreground-950">评论互动区</h2>
            </div>
            <p className="text-sm leading-relaxed text-foreground-500">
              本篇内容的读者讨论已在社区同步开启。当前版本为前端展示演示，评论功能将在后续接入真实数据后开放。
            </p>
            <div className="mt-4 flex flex-col sm:flex-row gap-3">
              <div className="flex-1 rounded-md border border-dashed border-background-300 bg-background-100 px-4 py-3 text-sm text-foreground-400">
                想说点什么？评论功能即将上线
              </div>
              <Link
                to="/community"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors"
              >
                去社区看看
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>

          <section className="mt-12">
            <SectionHeading
              title="相关推荐"
              subtitle="与你正在阅读的内容密切相关"
              icon="ri-survey-line"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {related.map((item) => (
                <ContentCard key={item.id} article={item} />
              ))}
            </div>
          </section>
        </article>

        <aside className="lg:col-span-4">
          <div className="sticky top-24 space-y-6">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950 mb-4">作者</h2>
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-500 text-background-50 font-bold">
                  {article.author.slice(0, 1)}
                </span>
                <div>
                  <p className="text-sm font-semibold text-foreground-900">{article.author}</p>
                  <p className="text-xs text-foreground-400">鲁啊鲁内容创作者</p>
                </div>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-foreground-500">
                持续关注{categoryName}领域的动态，为你带来及时、可靠的内容解读。
              </p>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950 mb-4">热门阅读</h2>
              <div className="space-y-3">
                {hot.map((item, index) => (
                  <Link
                    key={item.id}
                    to={`/detail/${item.id}`}
                    className="group flex items-start gap-3"
                  >
                    <span
                      className={`shrink-0 w-6 h-6 flex items-center justify-center rounded-md text-xs font-bold ${
                        index === 0 ? 'bg-accent-500 text-background-50' : 'bg-background-100 text-foreground-400'
                      }`}
                    >
                      {index + 1}
                    </span>
                    <span className="text-sm leading-snug text-foreground-700 group-hover:text-primary-700 transition-colors line-clamp-2">
                      {item.title}
                    </span>
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-lg bg-primary-50 border border-primary-100 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">加入讨论</h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                在社区里和更多读者交换观点，让你的想法被看见。
              </p>
              <Link
                to="/community"
                className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors"
              >
                进入社区
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </aside>
      </div>

      <div className="lg:hidden fixed bottom-16 left-0 right-0 z-40 border-t border-background-200 bg-background-50/95 backdrop-blur-md px-4 py-2.5 flex items-center gap-3">
        <button
          type="button"
          onClick={handleFavorite}
          className={`flex-1 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-medium transition-colors ${
            favorite
              ? 'bg-accent-500 text-background-50'
              : 'border border-background-200 text-foreground-600'
          }`}
        >
          <i className={favorite ? 'ri-heart-3-fill' : 'ri-heart-3-line'}></i>
          {favorite ? '已收藏' : '收藏'}
        </button>
        <button
          type="button"
          onClick={() => setShareOpen(true)}
          className="flex-1 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-200 px-4 py-2.5 text-sm font-medium text-foreground-600"
        >
          <i className="ri-share-forward-line"></i>
          分享
        </button>
      </div>

      <ShareModal
        open={shareOpen}
        title={article.title}
        onClose={() => setShareOpen(false)}
        onCopy={handleCopy}
        onSocial={handleSocial}
      />

      {toast ? (
        <div className="fixed bottom-28 lg:bottom-10 left-1/2 -translate-x-1/2 z-[70] rounded-full bg-foreground-950 px-5 py-2.5 text-sm text-background-50 animate-soft-in">
          {toast}
        </div>
      ) : null}
    </div>
  );
}